// src/components/profile/ProfileCTAs.tsx
"use client";

import React from 'react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { MessageCircleMore, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export default function ProfileCTAs(
  {
    personId,
    variant: _variant = 'default',
    size = 'md',
    layout = 'stack',
    className = '',
    context,
    contextId,
    showChat = true,
    showEmail = true,
    appearance = 'primary',
    buttonClassName = '',
  }:
  {
    personId: string | number;
    /** Retained for API compatibility; the visual treatment is now uniform with the site primary button. */
    variant?: 'default' | 'contrast' | 'brand';
    size?: 'sm' | 'md' | 'lg';
    layout?: 'stack' | 'inline';
    className?: string;
    context?: string;
    contextId?: string | number;
    showChat?: boolean;
    showEmail?: boolean;
    /** 'scrim' renders translucent white-on-image pills for use inside the profile banner. */
    appearance?: 'primary' | 'scrim';
    buttonClassName?: string;
  }
) {
  const { status } = useSession();
  const authed = status === 'authenticated';

  // apref=post-{id} lets ChatClient fire the application_submitted activation
  // event on the first send when the conversation started from a post.
  // NOT ?ref= : captureReferralCode() treats any top-level ref as an
  // affiliate code, stores it in the attribution cookie, and strips it.
  const chatTarget = (context === 'post' && contextId != null)
    ? `/chat?with=${encodeURIComponent(String(personId))}&apref=${encodeURIComponent(`post-${contextId}`)}`
    : `/chat?with=${encodeURIComponent(String(personId))}`;
  const emailTarget = (context && contextId != null)
    ? `/contact/${encodeURIComponent(String(personId))}/${encodeURIComponent(String(context))}/${encodeURIComponent(String(contextId))}`
    : `/contact/${encodeURIComponent(String(personId))}`;
  // Guests go to /register, not /login: most are first-time visitors from search
  // and the goal is new signups. /register carries returnTo so they land back on
  // the action after signing up, and offers a "Sign in" link for returning users.
  const chatHref = authed ? chatTarget : `/register?returnTo=${encodeURIComponent(chatTarget)}`;
  const emailHref = authed ? emailTarget : `/register?returnTo=${encodeURIComponent(emailTarget)}`;

  const wrapCls = layout === 'inline'
    ? cn('flex flex-wrap items-center justify-center gap-2', className)
    : cn('grid grid-cols-1 gap-2', className);

  const btnSize: 'sm' | 'lg' | 'default' = size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'default';
  const scrim = appearance === 'scrim';
  const btnVariant = scrim ? 'ghost' : 'primary';
  // The banner always sits on a dark image scrim in both themes, so the
  // secondary treatment there is white-on-scrim, not a theme surface.
  const btnCls = cn(
    scrim && 'bg-white/10 text-white ring-1 ring-white/25 hover:bg-white/20 hover:text-white backdrop-blur-sm',
    buttonClassName
  );

  return (
    <div className={wrapCls}>
      {showChat && (
        <Button asChild variant={btnVariant} size={btnSize} className={btnCls}>
          <Link href={chatHref}>
            <MessageCircleMore className="h-4 w-4" aria-hidden="true" />
            <span>Chat</span>
          </Link>
        </Button>
      )}
      {showEmail && (
        <Button asChild variant={btnVariant} size={btnSize} className={btnCls}>
          <Link href={emailHref}>
            <Mail className="h-4 w-4" aria-hidden="true" />
            <span>Email</span>
          </Link>
        </Button>
      )}
    </div>
  );
}
